import { type HTMLAttributes, forwardRef } from 'react'
import { cn } from '@/lib/utils'

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
    src?: string
    alt?: string
    size?: 'sm' | 'md' | 'lg' | 'xl'
    status?: 'online' | 'offline' | 'paused' | null
}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
    ({ className, src, alt, size = 'md', status, ...props }, ref) => {
        return (
            <div
                className={cn(
                    'relative flex-shrink-0 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden',
                    {
                        'w-8 h-8': size === 'sm',
                        'w-10 h-10': size === 'md',
                        'w-12 h-12': size === 'lg',
                        'w-20 h-20': size === 'xl',
                    },
                    className
                )}
                ref={ref}
                {...props}
            >
                {src ? (
                    <img src={src} alt={alt || 'Avatar'} className="w-full h-full object-cover" loading="eager" decoding="sync" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 font-medium">
                        {alt?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                )}
                {status && (
                    <span
                        className={cn(
                            'absolute bottom-0 right-0 block rounded-full ring-2 ring-white dark:ring-black',
                            {
                                'w-2.5 h-2.5': size === 'sm',
                                'w-3 h-3': size === 'md',
                                'w-3.5 h-3.5': size === 'lg',
                                'w-4 h-4': size === 'xl',
                            },
                            {
                                'bg-status-online': status === 'online',
                                'bg-status-offline': status === 'offline',
                                'bg-status-paused': status === 'paused',
                            }
                        )}
                    />
                )}
            </div>
        )
    }
)

Avatar.displayName = 'Avatar'

export { Avatar }
